/**
 * Session token store.
 *
 * Access token: memory only. Refresh token: localStorage (recorded trade-off,
 * approved architecture §11.5 — keeps a receptionist signed in across
 * restarts; sign-out and refresh failure clear it). Refresh is single-flight
 * so a burst of 401s produces exactly one refresh request.
 */

import { runtimeConfig } from '../config';
import { NetworkError, TimeoutError, parseErrorBody } from '../api/errors';
import { withDeadline } from '../api/deadline';

const REFRESH_KEY = 'zc.refreshToken';

export interface TokenSet {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

let accessToken: string | null = null;
let accessExpiresAt = 0; // epoch ms; 0 = unknown
let refreshing: Promise<string> | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

export function onSessionCleared(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function hasSessionMaterial(): boolean {
  return accessToken !== null || getStoredRefreshToken() !== null;
}

export function storeTokenSet(tokens: TokenSet): void {
  accessToken = tokens.access_token;
  accessExpiresAt =
    typeof tokens.expires_in === 'number' ? Date.now() + tokens.expires_in * 1000 : 0;
  if (tokens.refresh_token) {
    try {
      localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
    } catch {
      /* storage unavailable — session stays memory-only */
    }
  }
}

export function clearSession(): void {
  accessToken = null;
  accessExpiresAt = 0;
  refreshing = null;
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

/** True when the access token is missing or within 30s of expiry. */
export function accessTokenStale(): boolean {
  if (accessToken === null) return true;
  if (accessExpiresAt === 0) return false;
  return Date.now() > accessExpiresAt - 30_000;
}

/**
 * Refresh via Auth POST /auth/token/refresh { refresh_token } (verified
 * contract; persona-neutral). 401 = expired/revoked/reused → session cleared.
 *
 * ZOY-466: this call is awaited BEFORE every authenticated request, and its
 * promise is shared single-flight — so a stall here is the worst case in the
 * app: one hung refresh would hand the same never-settling promise to every
 * later caller and strand the whole session until reload. A deadline on the
 * fetch bounds it, and `finally` releases the single-flight slot on EVERY
 * outcome (timeout, network, HTTP refusal, malformed body) rather than on the
 * three paths that happened to remember to.
 */
export async function refreshAccessToken(): Promise<string> {
  if (refreshing) return refreshing;
  const refresh = getStoredRefreshToken();
  if (refresh === null) {
    clearSession();
    throw new Error('no refresh token');
  }
  refreshing = (async () => {
    const deadline = withDeadline();
    try {
      let res: Response;
      try {
        res = await fetch(`${runtimeConfig().authApiUrl}/auth/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refresh }),
          signal: deadline.signal,
        });
      } catch {
        throw deadline.timedOut() ? new TimeoutError() : new NetworkError();
      }
      if (!res.ok) {
        const err = parseErrorBody(res.status, await res.json().catch(() => null));
        if (res.status === 401) clearSession();
        throw err;
      }
      const tokens = (await res.json()) as TokenSet;
      storeTokenSet(tokens);
      return tokens.access_token;
    } finally {
      deadline.done();
      // Released on every outcome: a failed refresh must never leave the
      // single-flight slot occupied by a promise that will not settle again.
      refreshing = null;
    }
  })();
  return refreshing;
}

/**
 * ZOY-610 cross-tab: the refresh token is the shared session material. When
 * another tab REMOVES it (sign-out, or a refused refresh), this tab's session
 * is over too — it clears its memory and tells the UI. A rotated refresh
 * token written by another tab (a value replacing a value) is that same
 * session continuing, and is left alone. The `storage` event never fires in
 * the tab that made the change, so a tab never reacts to its own sign-out.
 */
export function subscribeSessionStorage(): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;
    if (e.key !== REFRESH_KEY && e.key !== null) return;
    if (e.newValue === null && accessToken !== null) clearSession();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
